import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { API_EMPLOYEE_TECH_STACK } from "../../../config/Endpoints.js";
import { Button, Modal, Select } from "../../components/ui/index.ts";
import { deleteApi, putApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import type {
  EditableTechStack,
  EmployeeTechStackRow,
  SkillLevel,
  TechStackCategory,
} from "./tech-stack-types.ts";
import styles from "./tech-stack-editor-modal.module.css";

const LEVEL_OPTIONS = [
  { value: "BEGINNER", label: "Beginner" },
  { value: "INTERMEDIATE", label: "Intermediate" },
  { value: "EXPERIENCED", label: "Experienced" },
];

type Props = {
  employee: EmployeeTechStackRow | null;
  categories: TechStackCategory[];
  onClose: () => void;
  onSaved: () => void;
};

export function TechStackEditorModal({
  employee,
  categories,
  onClose,
  onSaved,
}: Props) {
  const [skills, setSkills] = useState<EditableTechStack[]>([]);
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newTechStackId, setNewTechStackId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const masterById = useMemo(
    () =>
      new Map(
        categories.flatMap((category) =>
          category.techStacks.map((skill) => [
            skill.techStackId,
            {
              ...skill,
              category: category.category,
              colorCode: category.colorCode,
              logo: skill.logo || category.logo,
            },
          ] as const),
        ),
      ),
    [categories],
  );

  const availableTechStacks = useMemo(() => {
    const assignedIds = new Set(skills.map((skill) => skill.techStackId));
    return categories
      .filter((category) => String(category.categoryId) === newCategoryId)
      .flatMap((category) =>
        category.techStacks
          .filter((skill) => !assignedIds.has(String(skill.techStackId)))
          .map((skill) => ({
            value: String(skill.techStackId),
            label: skill.techStack,
          })),
      );
  }, [categories, newCategoryId, skills]);

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: String(category.categoryId),
        label: category.category,
      })),
    [categories],
  );

  useEffect(() => {
    if (!employee) return;
    setSkills(
      employee.techStacks.map((skill) => ({
        techStackId: String(skill.techStackId),
        skillLevel: skill.skillLevel ?? "BEGINNER",
      })),
    );
    setNewCategoryId("");
    setNewTechStackId("");
  }, [employee]);

  const addSkill = () => {
    if (!newTechStackId) {
      showError("Select a tech stack to add.");
      return;
    }
    setSkills((current) => [
      ...current,
      { techStackId: newTechStackId, skillLevel: "BEGINNER" },
    ]);
    setNewCategoryId("");
    setNewTechStackId("");
  };

  const save = async () => {
    if (!employee || skills.length === 0) return;
    if (newTechStackId) {
      showError("Select Add Tech Stack before saving the pending skill.");
      return;
    }
    setSaving(true);
    try {
      await putApi(
        `${API_EMPLOYEE_TECH_STACK}/${encodeURIComponent(
          String(employee.employeeId),
        )}`,
        accessToken(),
        {
          techStacks: skills.map((skill) => ({
            techStackId: Number(skill.techStackId),
            skillLevel: skill.skillLevel,
            primarySkill: false,
          })),
        },
      );
      showSuccess("Tech stack updated successfully.");
      onSaved();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to update tech stack.");
    } finally {
      setSaving(false);
    }
  };

  const removeSkill = async (techStackId: number) => {
    if (!employee) return;
    setDeletingIds((current) => new Set(current).add(techStackId));
    try {
      await deleteApi(
        `${API_EMPLOYEE_TECH_STACK}/${encodeURIComponent(
          String(employee.employeeId),
        )}/${encodeURIComponent(String(techStackId))}`,
        accessToken(),
      );
      setSkills((current) =>
        current.filter((skill) => Number(skill.techStackId) !== techStackId),
      );
      showSuccess("Tech stack removed successfully.");
      onSaved();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to remove tech stack.",
      );
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(techStackId);
        return next;
      });
    }
  };

  return (
    <Modal
      open={employee != null}
      onClose={onClose}
      title="Manage Tech Stack"
      size="lg"
      closeOnEscape
      className={styles.modal}
      bodyClassName={styles.modalBody}
      footer={
        <div className={styles.footerActions}>
          <Button variant="light-grey" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || skills.length === 0}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      {employee ? (
        <div className={styles.content}>
          <section className={styles.employeeSummary}>
            <div className={styles.avatar} aria-hidden>
              {employee.employeeName.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <h3 className={styles.employeeName}>{employee.employeeName}</h3>
              <p className={styles.employeeMeta}>
                {employee.employeeCode} ·{" "}
                {employee.designation || "Designation not available"}
              </p>
            </div>
          </section>

          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Assigned Tech Stacks</h3>
            <span className={styles.count}>{skills.length}</span>
          </div>

          <div className={styles.addRow}>
            <Select
              id="new-tech-stack-category"
              placeholder="Select category"
              value={newCategoryId}
              options={categoryOptions}
              onChange={(categoryId) => {
                setNewCategoryId(categoryId);
                setNewTechStackId("");
              }}
              searchable
              isClearable
            />
            <Select
              id="new-employee-tech-stack"
              placeholder={
                newCategoryId ? "Select tech stack" : "Select category first"
              }
              value={newTechStackId}
              options={availableTechStacks}
              onChange={setNewTechStackId}
              disabled={!newCategoryId}
              searchable
              isClearable
            />
            <Button
              startIcon={<Plus size={17} aria-hidden />}
              onClick={addSkill}
              disabled={!newTechStackId}
            >
              Add Tech Stack
            </Button>
          </div>

          <div className={styles.skillTable}>
            <div className={styles.tableHeader}>
              <span>Tech Stack</span>
              <span>Experience Level</span>
              <span>Actions</span>
            </div>
            {skills.length === 0 ? (
              <div className={styles.emptyState}>
                No tech stacks assigned. Use the fields above to add one.
              </div>
            ) : (
              skills.map((skill, index) => {
                const selected = masterById.get(Number(skill.techStackId));
                const name = selected?.techStack || `Tech Stack ${skill.techStackId}`;
                return (
                  <div className={styles.skillRow} key={skill.techStackId}>
                    <div className={styles.techStackCell}>
                      <span
                        className={styles.skillIcon}
                        style={{
                          "--skill-color": selected?.colorCode || "var(--info)",
                        } as React.CSSProperties}
                        aria-hidden
                      >
                        <span>{name.slice(0, 1).toUpperCase()}</span>
                        {selected?.logo ? (
                          <img
                            className={styles.skillLogo}
                            src={selected.logo.trim()}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                      </span>
                      <span>
                        <span className="sub-heading-md">{name}</span>
                        <small>{selected?.category}</small>
                      </span>
                    </div>
                    <Select
                      id={`employee-tech-level-${index}`}
                      value={skill.skillLevel}
                      options={LEVEL_OPTIONS}
                      onChange={(skillLevel) =>
                        setSkills((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? {
                                  ...item,
                                  skillLevel: skillLevel as SkillLevel,
                                }
                              : item,
                          ),
                        )
                      }
                    />
                    <button
                      type="button"
                      className={styles.removeButton}
                      aria-label={`Remove ${name}`}
                      onClick={() => removeSkill(Number(skill.techStackId))}
                      disabled={deletingIds.has(Number(skill.techStackId))}
                    >
                      <Trash2 size={18} aria-hidden />
                    </button>
                  </div>
                );
              })
            )}
          </div>

      
        </div>
      ) : null}
    </Modal>
  );
}
